/* eslint-disable @next/next/no-img-element */
import { useMemo, JSX } from 'react';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import SubscriptionPeriodMonth from '@/icons/SubscriptionPeriodMonth';
import SubscriptionPeriodYear from '@/icons/SubscriptionPeriodYear';
import { ClassNameProps } from '@/types/common.types';

import { useVerification } from '@/contexts/VerificationContext/useVerification';

import { PeriodType } from '@/api/types/user.types';

import { getMonthsFromDays } from '@/helpers/format-time.helpers';
import { normalizePrice } from '@/helpers/transformations.helpers';

import { PeriodEnum } from '@/enums/subscription-type.enums';

import { Card } from '@/components/Card';

import styles from '@/contexts/VerificationContext/VerificationModal/SubscriptionVerification/PeriodCard.module.scss';

// =================================================================

type PeriodCardProps = ClassNameProps &
  PeriodType & { period: PeriodEnum; isActive: boolean; onClick: () => void };

// =================================================================

const subscriptionPeriodImages: {
  [key in PeriodEnum]: JSX.Element;
} = {
  year: <SubscriptionPeriodYear />,
  month: <SubscriptionPeriodMonth />,
};

// =================================================================

export const PeriodCard = (props: PeriodCardProps) => {
  const { days, old_price, price, className, period, isActive, onClick } = props;

  const t = useTranslations();

  const { userCredentials } = useVerification();

  const month = useMemo(() => {
    return getMonthsFromDays(days);
  }, [days]);

  return (
    <Card
      className={clsx(styles.periodCard, className, {
        [styles.active]: isActive,
      })}
      onClick={onClick}
    >
      <div className={styles.leftSide}>
        <div className={styles.cardHeader}>
          <h4 className={styles.cardTitle}>
            {t('common.month', {
              count: month,
            })}
          </h4>
          {userCredentials?.discount && period === PeriodEnum.Month && (
            <p className={styles.cardSubtitle}>
              {t('common.firstlyMonth', {
                count: 1,
              })}
              <span className={styles.cardSubtitle__highlighted}>
                {t('verification.price', {
                  price: old_price,
                })}
              </span>
            </p>
          )}
        </div>

        <div className={styles.cardTitle}>
          {t('verification.price', {
            price: normalizePrice(price) ?? 0,
          })}
          <span className={styles.cardSubtitle}>
            /
            {month > 1
              ? t('common.year', {
                  count: 0,
                })
              : t('common.month', {
                  count: 0,
                })}
          </span>
        </div>
      </div>

      <div className={styles.rightSide}>{subscriptionPeriodImages[period]}</div>
    </Card>
  );
};
